import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  InputLabel,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, Theme } from "@mui/material/styles";

import { Icon } from "../Icon";

interface LabelRowProps {
  label: string;
  tooltip?: React.ReactNode;
  color?: string;
  disabled?: boolean;
  // True when we want to use MUI label components (InputLabel and its children).
  // This should be true in most cases. Should be false only when the LabelRow is used in a custom
  // component not in MUI FormControl, or when it is rendered by a MUI component which
  // already renders InputLabel and its children, such as TextField "label" prop.
  useMuiLabel: boolean;
  // True when we want to use native HTML <label /> component.
  // Should be true in most cases, except in the rare situations where
  // a label is already rendered by a MUI component which renders <label /> internally,
  // such as FormControlLabel component, and we still want to display a LabelRow above the field.
  useHtmlLabel: boolean;
  htmlFor?: string;
  sx?: SxProps<Theme>;
}

const TooltipIconWrapper = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  color: theme.palette.secondary[3],
  fontWeight: "light",
  cursor: "pointer",
  lineHeight: "8px",
}));

export const LabelRow = (props: LabelRowProps) => {
  const {
    label,
    tooltip,
    color,
    disabled = false,
    useMuiLabel = false,
    useHtmlLabel = false,
    htmlFor = "",
    sx = {},
  } = props;

  const content = (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        spacing: 1,
        ...sx,
      }}
    >
      <Typography sx={{ fontWeight: 400, color }} variant="regular">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="right">
          <TooltipIconWrapper>
            <Icon size="medium" icon={faQuestionCircle} />
          </TooltipIconWrapper>
        </Tooltip>
      )}
    </Stack>
  );

  if (useMuiLabel) {
    return (
      // MUI bad TS design, InputLabel extends FormLabel but FormLabel props are not supported in InputLabel props interface
      // @ts-ignore
      <InputLabel
        shrink
        disabled={disabled}
        sx={{ position: "relative", ...sx }}
        component={useHtmlLabel ? "label" : "div"}
        htmlFor={htmlFor}
      >
        {content}
      </InputLabel>
    );
  }

  return content;
};
